import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { FaArrowLeft, FaArrowRight } from 'react-icons/fa';

import Fancybox from '../../helpers/Fancybox';
import PageHeader from "../../components/common/PageHeader";
import ApiService from "../../api/ApiService";
import "../../css/darshan.css";
import GoToTop from "../../helpers/GoToTop";

const GalleryDetailPage = () => {
    const { type, date } = useParams();
    const navigate = useNavigate();

    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [festivalTitle, setFestivalTitle] = useState('');
    const [top, setTop] = useState(0);

    const festRef = useCallback((node) => {
        if (node) {
            setTop(node.offsetTop - 115);
        }
    }, []);

    useEffect(() => {
        const fetchGalleries = async () => {
            setLoading(true);
            setError(null);

            try {
                const response = await ApiService.fetchDailyDarshanGalleries();
                const galleryTypeData = response?.[type?.toLowerCase()];

                if (!galleryTypeData) {
                    setError(`No gallery found for type: ${type}`);
                    setImages([]);
                    return;
                }

                const matchedImages = [];
                const targetDate = moment(date).format('YYYY-MM-DD');

                for (const entry of galleryTypeData) {
                    for (const img of entry.images) {
                        const formattedDate = moment(img.uploadedAt).format('YYYY-MM-DD');
                        if (formattedDate === targetDate) {
                            matchedImages.push({
                                url: `${process.env.REACT_APP_S3_URL}/uploads/${type}/${img.filename}`,
                                caption: img.caption || `${type?.replace(/-/g, ' ')} - ${moment(date).format('MMMM D, YYYY')}`
                            });
                        }
                    }
                }

                if (type === 'festival-darshan' && galleryTypeData.length > 0) {
                    const foundEntry = galleryTypeData.find(entry =>
                        entry.images.some(img =>
                            moment(img.uploadedAt).format('YYYY-MM-DD') === targetDate
                        )
                    );
                    if (foundEntry && foundEntry.festival_title) {
                        setFestivalTitle(foundEntry.festival_title);
                    } else {
                        setFestivalTitle('');
                    }
                }

                if (matchedImages.length === 0) {
                    setError(`No images found for ${type?.replace(/-/g, ' ')} on ${moment(date).format('MMMM D, YYYY')}`);
                }

                setImages(matchedImages);
            } catch (error) {
                console.error("Error fetching galleries:", error);
                setError("Failed to load gallery images. Please try again later.");
                setImages([]);
            } finally {
                setLoading(false);
            }
        };

        fetchGalleries();
    }, [type, date]);

    const isFeatureImage = (index) => {
        return index === 0 || index === 3 || index === 7 || index % 8 === 0;
    };

    const typeTitles = {
        'festival-darshan': "Festival Darshan",
        mangala: "Mangala Darshan",
        sringar: "Sringar Darshan",
    };

    return (
        <>
            <PageHeader title="Daily Darshan" imgSrc="/images/page-header/03.webp" />

            <section className="gallery-section py-5" ref={festRef}>
                <div className="container">
                    {/* Heading */}
                    <div className="row">
                        <div className="col-lg-7 mx-auto">
                            <div className="heading text-center mb-2">
                                <h2 className="head-1">
                                    {type === 'festival-darshan' && festivalTitle
                                        ? `${festivalTitle} - ${moment(date).format("MMMM D, YYYY")}`
                                        : `${typeTitles[type] || ''} - ${moment(date).format("MMMM D, YYYY")}`}
                                </h2>
                            </div>
                        </div>
                        <div className="col-md-5">
                            <div className="p-3 rounded-4" style={{ backgroundColor: 'white' }}>
                                {/* Dropdown */}
                                <select
                                    value={type}
                                    onChange={(e) => navigate(`/daily-darshan-gallery/${e.target.value}/${date}`)}
                                    className="form-select mb-3 text-center fw-bold text-warning"
                                    style={{ borderRadius: '10px', textAlignLast: 'center', borderColor: "#f5bb3c" }}
                                >
                                    <option value="festival-darshan">Festival Darshan</option>
                                    <option value="mangala">Mangala Darshan</option>
                                    <option value="sringar">Sringar Darshan</option>
                                </select>

                                {/* Prev | Date | Next */}
                                <div className="d-flex justify-content-between align-items-center">
                                    <button
                                        onClick={() =>
                                            navigate(`/daily-darshan-gallery/${type}/${moment(date).subtract(1, 'day').format('YYYY-MM-DD')}`)
                                        }
                                        className="btn btn-link text-warning text-decoration-none"
                                    >
                                        <FaArrowLeft className="me-1" style={{ fontSize: '0.9rem' }} /> Previous
                                    </button>

                                    <div className="fw-semibold text-warning" style={{ fontSize: '1.1rem' }}>
                                        {moment(date).format('DD MMMM')}
                                    </div>

                                    <button
                                        onClick={() =>
                                            navigate(`/daily-darshan-gallery/${type}/${moment(date).add(1, 'day').format('YYYY-MM-DD')}`)
                                        }
                                        className="btn btn-link text-warning text-decoration-none"
                                    >
                                        Next <FaArrowRight className="ms-1" style={{ fontSize: '0.9rem' }} />
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Content */}
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3">Loading gallery images...</p>
                        </div>
                    ) : error ? (
                        <div className="text-center py-5">
                            <div className="alert alert-info" role="alert">
                                {error}
                            </div>
                        </div>
                    ) : (
                        <div className="masonry-gallery">
                            {images.map((image, idx) => (
                                <div className={`masonry-item ${isFeatureImage(idx) ? 'feature-image' : ''}`} key={idx}>
                                    <Fancybox>
                                        <a
                                            data-fancybox="gallery"
                                            href={image.url}
                                            data-download-src={image.url}
                                            data-caption={image.caption}
                                            className="gallery-link"
                                        >
                                            <img
                                                src={image.url}
                                                alt={image.caption}
                                                className="img-fluid"
                                                loading="lazy"
                                            />
                                        </a>
                                    </Fancybox>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>
            <GoToTop top={top} />
        </>
    );
};

export default GalleryDetailPage;
